// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
// This component renders a single audit event as a list item, used on record
// detail pages (e.g., Project Details, Customer Details) in the Audit Log tab.
import { AuditEvent } from '@/audit/api/types';
import { AuditEventLabel } from '@/audit/components/audit-event-label';
import { AuditPropertyLabel } from '@/audit/components/audit-property-label';
import { MultiTimezoneTooltip } from '@/components/multi-timezone-tooltip';
import { AccessTimeOutlined } from '@/constants/hardfin-standard-icons';
import {
  Avatar,
  Stack,
  Theme,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { PaletteColor } from '@mui/material/styles/createPalette';

type AuditEventListItemVariant = 'compact' | 'full';

const mergeOrange = {
  light: '#FFB74D',
  main: '#FF9800',
  dark: '#F57C00',
  contrastText: '#fff',
} as PaletteColor;

const DEFAULT_VARIANT: AuditEventListItemVariant = 'full';

interface Props {
  /**
   * An audit event to render.
   */
  event: AuditEvent;

  /**
   * The variant of the list item.
   *
   * @default 'full'
   */
  variant?: AuditEventListItemVariant;
}

/**
 * Gets the initials from a name string
 */
const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase();
};

/**
 * Gets the badge configuration for different audit actions
 */
const getActionBadgeConfig = (action: string, theme: Theme) => {
  switch (action) {
    case 'CREATE':
      return {
        color: theme.palette.success.main,
        label: 'Create',
      };
    case 'UPDATE':
      return {
        color: theme.palette.info.main,
        label: 'Update',
      };
    case 'DELETE':
      return {
        color: theme.palette.error.main,
        label: 'Delete',
      };
    case 'MERGE':
      return {
        color: mergeOrange.main,
        label: 'Merge',
      };
    default:
      return {
        color: theme.palette.grey[500],
        label: action,
      };
  }
};

/**
 * Renders a single audit event as a list item with modern styling.
 */
export function AuditEventListItem(props: Props) {
  const theme = useTheme();
  const isDelete = props.event.context.action === 'DELETE';
  const variant = props.variant ?? DEFAULT_VARIANT;

  const actionConfig = getActionBadgeConfig(props.event.context.action, theme);
  const avatarColor = theme.palette.primary.main;
  const initials = getInitials(props.event.context.actor.name);

  if (variant === 'full') {
    return (
      <Stack
        component="li"
        direction="row"
        spacing={2}
        alignItems="flex-start"
        sx={{
          '&:not(:last-child)': {
            paddingBottom: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        {/* User Avatar */}
        <Tooltip title={props.event.context.actor.name} arrow placement="top">
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: alpha(avatarColor, 0.1),
              color: avatarColor,
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            {initials}
          </Avatar>
        </Tooltip>

        <Stack spacing={1} flex={1} minWidth={0}>
          {/* Event Description */}
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <AuditEventLabel
              context={props.event.context}
              eventIdentifier={props.event.eventIdentifier}
            />
            <Typography
              component="span"
              variant="caption"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: '4px',
                bgcolor: alpha(actionConfig.color, 0.1),
                color: actionConfig.color,
                fontWeight: 500,
                flexShrink: 0,
              }}
            >
              {actionConfig.label}
            </Typography>
          </Stack>

          {/* Properties */}
          <Stack spacing={0.5} sx={{ pl: 0 }}>
            {props.event.context.properties
              .filter((property) => property.redacted !== true)
              .map((property) => (
                <AuditPropertyLabel
                  key={property.id}
                  property={property}
                  isDelete={isDelete}
                  action={props.event.context.action}
                  context={props.event.context}
                />
              ))}
          </Stack>
        </Stack>

        {/* Timestamp with multi-timezone tooltip */}
        <MultiTimezoneTooltip timestamp={props.event.triggeredAt}>
          <Stack
            component="span"
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={0.5}
            sx={{ flexShrink: 0 }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
              }}
            >
              {props.event.triggeredAt.toFormat('MMM d, yyyy')}
            </Typography>
            <AccessTimeOutlined
              fontSize="small"
              sx={{ color: theme.palette.grey[400] }}
            />
          </Stack>
        </MultiTimezoneTooltip>
      </Stack>
    );
  }

  // Compact variant
  return (
    <Stack
      component="li"
      direction="row"
      spacing={2}
      alignItems="flex-start"
      sx={{
        py: 1.5,
        '&:not(:last-child)': {
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Tooltip title={props.event.context.actor.name} arrow placement="top">
        <Typography
          variant="caption"
          sx={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            bgcolor: alpha(avatarColor, 0.1),
            color: avatarColor,
            fontWeight: 500,
          }}
        >
          {initials}
        </Typography>
      </Tooltip>

      <Stack spacing={0.5} flex={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AuditEventLabel
            context={props.event.context}
            eventIdentifier={props.event.eventIdentifier}
          />
          <Typography
            component="span"
            variant="caption"
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: '4px',
              bgcolor: alpha(actionConfig.color, 0.1),
              color: actionConfig.color,
              fontWeight: 500,
            }}
          >
            {actionConfig.label}
          </Typography>
        </Stack>

        <Stack spacing={0.5}>
          {props.event.context.properties
            .filter((property) => property.redacted !== true)
            .map((property) => (
              <AuditPropertyLabel
                key={property.id}
                property={property}
                isDelete={isDelete}
                action={props.event.context.action}
                context={props.event.context}
              />
            ))}
        </Stack>
      </Stack>

      <MultiTimezoneTooltip timestamp={props.event.triggeredAt}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ whiteSpace: 'nowrap' }}
        >
          {props.event.triggeredAt.toFormat('MMM d yyyy, h:mm a')}
        </Typography>
      </MultiTimezoneTooltip>
    </Stack>
  );
}
